import * as Logger from '@navigator/frontend-logger';
import { IACCommunication } from '..';
import { Config } from '../config';
import { IACUsecase } from '../iacType/iacUsecase';
import { ApplicationLifeCycle } from '../interface';
import { IACMessageData } from '../transport/iacProtocol';

/*
 * This are the informations which are needed to send
 * post messaged to the application.
 */
export interface IACSendingInformation {
  contentWindow: Window;
  iacAppname: string;
  iacVersion: number;
}

export interface ApplicationConfig {
  view_mode: string;
  sysname: string;
  feedback: boolean;
  display_enabled: boolean;
  logo_title: string;
  title: string;
  loading_text?: string;
  // url_map is an object with the URL key as key and the
  // value is an URL string. The object must have a default URL key
  // and optional other keys.
  url_map: any;
  iac: {
    initial_usecase: IACMessageData;
    fallback?: FallbackConfig;
  };
  dialog_properties?: {
    width: number;
    widthUnit?: string;
    height: number;
    heightUnit?: string;
    modal: boolean;
    top: number;
    titlebar: string;
    draggable: boolean;
    background: boolean;
    borderstyle: boolean;
    canvas_size: string;
  };
  wait_for_application_ready?: boolean;
  start_in_background?: boolean;
  allow_redirect_navigator?: boolean;
}

export interface FallbackConfig {
  timeout: number;
  url: string;
  connection_fallback_pixel?: number;
}

/*
 * Interface implementation of any application representation.
 * SAK:
 * I guess this is a good connection point to the poseidon world.
 *
 * Known issues:
 * This class handles a splash screen (tracker), I don't currently know why?
 */
export class BaseApplication {

  protected iacConnected = false;

  public config: ApplicationConfig = {} as any;
  public type: string = null;
  public iacVersion: number = null;
  public iacFocusCounter = 0;
  public iacResumeFocusCounter = 0;
  public iacBlurCounter = 0;
  public iacRestartCounter = 0;
  public iacDisabledCounter = 0;
  public iacEnabledCounter = 0;

  public isIacConnected(): boolean {
    return this.iacConnected;
  }

  public setIacConnected(value: boolean): void {
    this.iacConnected = value;
  }

  public resetIacConnected(): void {
    this.iacConnected = false;
  }

  public getSysname(): string {
    return this.config.sysname;
  }

  public open(): void {
    throw new Error('This method is abstract and must be implemented.');
  }

  public reopen(urlKey?: string): void {
    ApplicationLifeCycle.reopenApplication(this, urlKey);
  }

  /* istanbul ignore next function */
  public close(): void {
    // override method if special handling is needed.
  }

  /* istanbul ignore next function */
  public reinitialize(): void {
    // override method if special handling is needed.
  }

  public focus(): void {
    ApplicationLifeCycle.onFocus(this);
  }

  public unfocus(): void {
    ApplicationLifeCycle.onUnFocus(this);
  }

  public isOnFocus(): boolean {
    throw new Error('This method is abstract and must be implemented.');
  }

  public isRunning(): boolean {
    throw new Error('This method is abstract and must be implemented.');
  }

  public updateConfig(): void {
    this.config = Config.get().navigator.apps[this.getSysname()];
  }

  public setURL(key: string, url: string): void {
    this.config.url_map[key || 'default'] = url || 'about:blank';

    // FIXME This update is needed otherwise the third party config will not be in sync.
    // The main issue is that the app config is writable. But the app config should
    // only be read-only. The writable feature should be removed when the third party
    // stuff will be refactored.
    const apps: {
      [key: string]: ApplicationConfig;
    } = {};
    apps[this.getSysname()] = this.config;
    Config.extend({ navigator: { apps } });
  }

  public getInitialUsecase(): IACUsecase {
    return IACCommunication.createUsecaseFromConfig(this.config.iac.initial_usecase);
  }

  public getIACSendingInformation(): IACSendingInformation {
    if (!this.isRunning()) {
      throw new Error(`Cannot resolve IAC sending information: Application ${this.getSysname()} is not running!`);
    }

    let frame: HTMLIFrameElement;
    let contentWindow: Window;
    try {
      frame = this.getFrameReference();
      contentWindow = frame.contentWindow;
    } catch (ex) {
      Logger.errorWithScope('contentWindow', {
          href: window.location.href,
          frameSrc: frame ? frame.src : undefined,
          iacApp: this.getSysname()
        }, 'Could not get contentWindow', ex);
      return null;
    }

    return {
      contentWindow,
      iacAppname: this.getSysname(),
      iacVersion: this.iacVersion,
    };
  }

  protected getFrameReference(): HTMLIFrameElement {
    return window.document.getElementById(`thirdPartyFrame_${this.getSysname()}`) as HTMLIFrameElement;
  }

  public hasFeedback(): boolean {
    return !!this.config.feedback;
  }
}
